import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/sl'
import relativeTime from 'dayjs/plugin/relativeTime'

import { INTL_LOCALE, Locale } from '@/enums/locale'
import i18n from '@/libs/i18n'
import { dayAsDate, dayNumber, dayParts, dayString, isDay, todayInLjubljana } from '@/utils/calendar'

dayjs.extend(relativeTime)

/*
 * The configured instance (vue-spec libraries.md): everything that formats an instant takes
 * it from here, so there is one set of plugins and one set of locales — Slovenian and English.
 */
export { dayjs }

const locale = (): Locale => {
    const value = (i18n.global.locale as unknown as { value: string }).value

    return value === Locale.En ? value : Locale.Sl
}

/** The BCP 47 tag the current language hands to Intl. */
export const intlLocale = (): string => INTL_LOCALE[locale()]

/*
 * HOW A DATE IS WRITTEN, per language: Slovenian "23. 9. 2026", English "23 Sep 2026".
 * `dayMonth` is a date without its year.
 */
const PATTERNS: Record<Locale, { day: string, dayMonth: string, dateTime: string, weekdayDay: string }> = {
    [Locale.Sl]: { day: 'D. M. YYYY', dayMonth: 'D. M.', dateTime: 'D. M. YYYY HH:mm', weekdayDay: 'dd, D. M.' },
    [Locale.En]: { day: 'D MMM YYYY', dayMonth: 'D MMM', dateTime: 'D MMM YYYY HH:mm', weekdayDay: 'ddd D MMM' },
}

const patterns = () => PATTERNS[locale()]

/*
 * A DAY ("YYYY-MM-DD") is not an instant: dayjs reads a bare date as that date's local
 * midnight and writes it back unchanged, never through a time zone.
 */
const asDay = (value: string) => dayjs(value).locale(locale())

/** A day without its year when it is this year's: "23. 9." — else the full date. */
export const formatDayShort = (value?: string | null): string => {
    if (!isDay(value)) return ''

    const thisYear = dayParts(dayNumber(value)).year === dayParts(dayNumber(todayInLjubljana())).year

    return asDay(value).format(thisYear ? patterns().dayMonth : patterns().day)
}

/** A day number's date without the year — a Gantt's week label: "22. 9." / "22 Sep". */
export const formatDayMonth = (value: number): string => asDay(dayString(value)).format(patterns().dayMonth)

/** A span of days: "1. 10. – 10. 10.", or one day when both ends are the same. */
export const formatDayRange = (start?: string | null, due?: string | null): string => {
    if (start && due && start !== due) return `${formatDayShort(start)} – ${formatDayShort(due)}`

    return formatDayShort(due || start)
}

/** A day with its weekday: "sr, 23. 9." / "Wed 23 Sep". */
export const formatDayWithWeekday = (value: number): string => asDay(dayString(value)).format(patterns().weekdayDay)

/** An instant (RFC 3339) in the reader's zone: "23. 9. 2026 14:05". */
export const formatDateTime = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).format(patterns().dateTime)
}

/** An instant's day in the reader's zone: "23. 9. 2026". */
export const formatDate = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).format(patterns().day)
}

/*
 * How long ago something happened, in words: "pred 3 minutami", "3 minutes ago". Day.js carries
 * each language's phrasing, the Slovenian dual included, so it is delegated rather than counted
 * into a house string.
 */
export const agoLabel = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).fromNow()
}

/** A month in the reader's language: "september 2026", "September 2026". */
export const formatMonth = (value: number, style: 'long' | 'short' = 'long', withYear = true): string =>
    new Intl.DateTimeFormat(intlLocale(), {
        month:    style,
        year:     withYear ? 'numeric' : undefined,
        timeZone: 'UTC',
    }).format(dayAsDate(value))

/** The weekday's shortest name, for a Gantt's day header. */
export const formatWeekdayNarrow = (value: number): string =>
    new Intl.DateTimeFormat(intlLocale(), { weekday: 'short', timeZone: 'UTC' }).format(dayAsDate(value))
